import { memo, useCallback } from "react";
import { SearchBase } from "./base";

export interface SearchElement {
  title: string;
  description: string;
  url: string;
}

interface SearchElementResultProps {
  elements?: SearchElement[] | null;
  totalCount: number;
  currentIndex?: number;
  onItemClick?: (index: number) => void;
}

interface ElementCardProps {
  element: SearchElement;
  index: number;
  isActive: boolean;
  onClick: () => void;
}

function ElementCard({
  element,
  index,
  isActive,
  onClick,
}: ElementCardProps) {
  return (
    <div
      role="option"
      tabIndex={0}
      id={`search-result-element-${index}`}
      data-result-index={index}
      aria-selected={isActive}
      aria-label={element.title}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      className={`
        w-full p-3 rounded-md cursor-pointer transition-colors duration-150
        ${isActive ? "bg-highlight/10" : "hover:bg-primary-bg/50"}
        focus:outline-none
      `}
    >
      <div className="flex flex-col gap-1">
        <h4
          className={`text-sm font-semibold truncate text-primary-text`}
        >
          {element.title}
        </h4>
        <p
          className={`text-sm line-clamp-2 text-primary-text-secondary`}
        >
          {element.description}
        </p>
        <a
          href={element.url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="text-sm text-highlight hover:underline truncate"
        >
          {element.url}
        </a>
      </div>
    </div>
  );
}


const MemoizedElementCard = memo(ElementCard);

export function SearchElementResult({
  elements,
  currentIndex = -1,
  onItemClick,
}: SearchElementResultProps) {
  const handleElementClick = useCallback(
    (index: number, url: string) => {
      let clickTimeout: NodeJS.Timeout;

      return () => {
        if (clickTimeout) clearTimeout(clickTimeout);
        onItemClick?.(index);

        clickTimeout = setTimeout(() => {
          window.open(url, "_blank", "noopener,noreferrer");
        }, 150);

        return () => clearTimeout(clickTimeout);
      };
    },
    [onItemClick]
  );

  if (!elements?.length) return null;

  return (
    <SearchBase title="Cursos" aria-live="polite">
      <div className="space-y-2 animate-in fade-in duration-300">
        {elements.map((element, index) => (
          <MemoizedElementCard
            key={`${element.title}-${index}`}
            element={element}
            index={index}
            isActive={currentIndex === index}
            onClick={handleElementClick(index, element.url)}
          />
        ))}
      </div>
    </SearchBase>
  );
}
